import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { TextField } from '@mui/material';
import { DeleteDialogCustomField } from 'common/components/deleteDialog/DeleteDialogCustomField';
import { selectCurrentEntityType, selectIsDialogOpen,setDialogOpen } from 'common/components/deleteDialog/deleteDialogSlice';
import { extractStatusCode } from 'common/services/api/apiHelpers';
import { deleteUser } from 'common/services/api/Users/Users.thunk';
import { DEFAULT_ERROR_MESSAGE } from 'common/utils/errorHandlers';
import { useSnackbar } from 'notistack';

import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { DeleteDialog } from '../../common/components/deleteDialog/DeleteDialog';
import { DeleteDialogContentProps, DrawerEntitiesNames } from '../../common/types/TypeDefs';

export function UserDetailsDeleteDialog({
    editingRow,
    updatePageCallback
}: Readonly<DeleteDialogContentProps<{ userFullName: string; userId: string; } | null>>) {
    const currentDialogEntity = useAppSelector(selectCurrentEntityType);
    const [deleteButtonDisabled, setDeleteButtonDisabled] = useState<boolean>(true);
    const { userFullName, userId } = editingRow ?? {};
    const { enqueueSnackbar } = useSnackbar();
    const [processing, setProcessing] = useState<boolean>(false);
    const dispatch = useAppDispatch();
    const isDeleteDialogOpened = useAppSelector(selectIsDialogOpen);
    const navigate = useNavigate();

    const successHandler = () => {
        dispatch(setDialogOpen(false));
        enqueueSnackbar(`${userFullName} has been deleted.`, { variant: 'success' });
        setProcessing(false);
        navigate('/users');
    };

    const handleDeleteActionError = (error: { message: string; }) => {
        const code = extractStatusCode(error.message);
        if (code === 409) {
            enqueueSnackbar(
                'The User is still a member of one or more Tenants. Please remove all Tenant memberships prior to deleting the User',
                { variant: 'error' }
            );
            return;
        }
        enqueueSnackbar(
            'There was an error while deleting this User.' +
            DEFAULT_ERROR_MESSAGE,
            { variant: 'error' }
        );
        setProcessing(false);
    };

    useEffect(() => {
        if(!isDeleteDialogOpened) {
            setDeleteButtonDisabled(true);
        }
    }, [isDeleteDialogOpened]);

    const userProfileDeleteCallback = useCallback(() => {
        if (userId) {
            const requestPayloadOptions = {
                userId
            };
            setProcessing(true);
            dispatch(deleteUser(requestPayloadOptions))
                .unwrap()
                .then(() => {
                    updatePageCallback?.();
                    successHandler();
                })
                .catch((error) => {
                    handleDeleteActionError(error);
                })
                .finally(()=> {
                    setProcessing(false);
                });
        }
    }, [userId]);

    if (currentDialogEntity !== DrawerEntitiesNames.UserProfile) {
        return null;
    }

    return  (
        <DeleteDialog
            deleteCallback={userProfileDeleteCallback}
            processing={processing ?? false}
            customDeleteTitle={`Are you sure you want to delete ${userFullName} from Palm?`}
            deleteButtonDisabled={deleteButtonDisabled}
        >
            <DeleteDialogCustomField
                fieldName='User'
                fieldValue={userFullName}
            />
            <TextField
                inputProps={{ 'data-testid': 'text-field-test-id' }}
                onChange={(e)=> {
                    if (e.target.value === 'DELETE') {
                        setDeleteButtonDisabled(false);
                    } else {
                        setDeleteButtonDisabled(true);
                    }
                }}
                sx={{ marginTop: '1rem', display: 'flex', '& .MuiOutlinedInput-root': { height: '35px' } }}
                id="outlined-helperText"
                helperText="Type  'DELETE'  to confirm"
            />
        </DeleteDialog>
    );
}
